import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDownAZ,
  ArrowDownNarrowWide,
  ArrowUpNarrowWide,
  ArrowUpZA,
  Ban,
  Download,
  Eye,
  Pencil,
  Send,
} from "lucide-react";

import { createAxiosInstance } from "api/axiosConfig";
import { CancelImmunisationModal } from "components/Modal/CancelImmunisationModal";
import { DataTable } from "components/ui/DataTable";
import { Dialog } from "components/ui/Dialog";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { PpsnClaimBlockedDialog } from "features/immunizations/components/PpsnClaimBlockedDialog";
import { useDeleteImmunisation } from "features/immunizations/hooks/useDeleteImmunisation";
import { useSubmitVaccineToHealthlink } from "features/immunizations/hooks/useSubmitVaccineToHealthlink";
import { formatDate } from "utils/dateFormatter";
import {
  canSubmitToHealthlink,
  formatImmunizationStatus,
  formatImmunizationStatusSimplified,
  getImmunizationStatusColors,
  isImmunizationActionRequired,
  isImmunizationCancelled,
  isImmunizationPending,
  isImmunizationRejected,
} from "utils/immunizationStatus";
import { pathGenerators } from "utils/routes";

import { AddVaccineForm } from "../../../../patient/PatientImmunizationsPage/components/AddVaccineForm/AddVaccineForm";
import { MANUFACTURER_OPTIONS } from "../../../../patient/PatientImmunizationsPage/components/AddVaccineForm/constants";
import { VaccineDetails } from "../../../../patient/PatientImmunizationsPage/components/VaccineDetails/VaccineDetails";

const SORT_OPTIONS = [
  {
    field: "dateGiven",
    direction: "desc",
    label: "Date (Newest first)",
    icon: ArrowDownNarrowWide,
  },
  {
    field: "dateGiven",
    direction: "asc",
    label: "Date (Oldest first)",
    icon: ArrowUpNarrowWide,
  },
  {
    field: "patient.firstName",
    direction: "asc",
    label: "Patient Name (A-Z)",
    icon: ArrowDownAZ,
  },
  {
    field: "patient.firstName",
    direction: "desc",
    label: "Patient Name (Z-A)",
    icon: ArrowUpZA,
  },
  {
    field: "vaccineName",
    direction: "asc",
    label: "Vaccine Name (A-Z)",
    icon: ArrowDownAZ,
  },
  {
    field: "vaccineName",
    direction: "desc",
    label: "Vaccine Name (Z-A)",
    icon: ArrowUpZA,
  },
];

const getReactionPillColors = (reaction) => {
  if (!reaction || reaction === "-") return PILL_COLORS.reactionUnknown;
  const upper = reaction.toUpperCase();
  if (upper === "YES") return PILL_COLORS.reactionYes;
  if (upper === "NO") return PILL_COLORS.reactionNo;
  return PILL_COLORS.reactionUnknown;
};

const formatReactionText = (reaction) => {
  if (!reaction || reaction === "-") return "-";
  const upper = reaction.toUpperCase();
  if (upper === "YES") return "Yes";
  if (upper === "NO") return "No";
  if (upper === "UNKNOWN") return "Unknown";
  return reaction.charAt(0).toUpperCase() + reaction.slice(1).toLowerCase();
};

const getManufacturerDisplay = (immunisation) => {
  if (immunisation.vaccineManufacturer) return immunisation.vaccineManufacturer;
  if (immunisation.vaccineCodeMvxCode) {
    const match = MANUFACTURER_OPTIONS.find(
      (m) => m.value === immunisation.vaccineCodeMvxCode
    );
    return match ? match.label : immunisation.vaccineCodeMvxCode;
  }
  return "-";
};

const fetchHealthlinkImmunisations = async (tenantVal, page, rows, filters) => {
  const axiosInstance = createAxiosInstance();
  const params = new URLSearchParams({
    page: page.toString(),
    rows: rows.toString(),
    ...filters,
  });
  const response = await axiosInstance.get(
    `${tenantVal}/patientimmunisation/healthlink-vaccines?${params}`
  );
  return response.data;
};

export const HealthlinkImmunisationsTable = ({
  tenantVal,
  advancedFilters = {},
}) => {
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("dateGiven");
  const [sortDirection, setSortDirection] = useState("desc");
  const [search, setSearch] = useState("");
  const [detailsItem, setDetailsItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [cancelItem, setCancelItem] = useState(null);

  const [rows, setRows] = useState(20);

  const apiFilters = useMemo(
    () => ({
      patientName: search,
      vaccineName: advancedFilters.vaccineName || "",
      dateGivenFrom: advancedFilters.dateGivenFrom || "",
      dateGivenTo: advancedFilters.dateGivenTo || "",
      type: advancedFilters.vaccineType || "",
      batchNo: advancedFilters.batchNo || "",
      vaccineManufacturer: advancedFilters.vaccineManufacturer || "",
      sortBy: sortField,
      sortDirection,
    }),
    [search, advancedFilters, sortField, sortDirection]
  );

  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      "healthlinkImmunisations",
      tenantVal,
      currentPage,
      rows,
      apiFilters,
    ],
    queryFn: () =>
      fetchHealthlinkImmunisations(tenantVal, currentPage, rows, apiFilters),
    enabled: !!tenantVal,
  });

  const { mutate: deleteImmunisation, isPending: isDeletingImmunisation } =
    useDeleteImmunisation();

  const {
    submitVaccine,
    isSubmitting,
    ppsnBlockedPatientId,
    clearPpsnBlocked,
  } = useSubmitVaccineToHealthlink({ tenantVal, onSubmitted: refetch });

  const handleDownloadSingle = useCallback(
    async (item) => {
      try {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.post(
          `${tenantVal}/patientimmunisation/print`,
          { patientId: item.patient?.id, immunisationIds: [item.id] },
          { responseType: "blob" }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `immunisation-report-${item.patient?.id}.pdf`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Download failed:", error);
        toast.error("Failed to download immunisation report");
      }
    },
    [tenantVal]
  );

  const handlePatientClick = useCallback(
    (immunisation) => {
      const patientId = immunisation.patient?.id;
      if (!patientId) return;
      navigate(pathGenerators.patient.immunizations(patientId));
    },
    [navigate]
  );

  const handleSortChange = useCallback((field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    setCurrentPage(1);
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "dateGiven",
        header: "Date Given",
        width: "110px",
        render: (item) => formatDate(item.dateGiven) || "-",
      },
      {
        id: "patientName",
        header: "Patient Name",
        width: "200px",
        render: (item) =>
          `${item.patient?.firstName || ""} ${
            item.patient?.surname || ""
          }`.trim() || "-",
        getTooltipText: (item) =>
          `${item.patient?.firstName || ""} ${
            item.patient?.surname || ""
          }`.trim() || "-",
        onClick: (item) => item.patient?.id && handlePatientClick(item),
      },
      {
        id: "vaccineCodeName",
        header: "Name",
        width: "400px",
        render: (item) => item.vaccineCodeName || "-",
        getTooltipText: (item) => item.vaccineCodeName || "-",
      },
      {
        id: "type",
        header: "Type",
        width: "180px",
        render: (item) => item.type || "-",
        getTooltipText: (item) => item.type || "-",
      },
      {
        id: "status",
        header: "Status",
        width: "190px",
        render: (item) => {
          const colors = getImmunizationStatusColors(item.status);
          return (
            <Tooltip
              title={formatImmunizationStatus(item.status)}
              placement="top"
              enterDelay={200}
            >
              <span>
                <Pill
                  color={colors.color}
                  bg={colors.bg}
                  border={colors.border}
                >
                  {formatImmunizationStatusSimplified(item.status)}
                </Pill>
              </span>
            </Tooltip>
          );
        },
      },
      {
        id: "reaction",
        header: "Reaction",
        width: "120px",
        render: (item) => {
          const colors = getReactionPillColors(item.reaction);
          return (
            <Pill color={colors.color} bg={colors.bg} border={colors.border}>
              {formatReactionText(item.reaction)}
            </Pill>
          );
        },
      },
      {
        id: "batchNo",
        header: "Batch No",
        width: "130px",
        render: (item) => item.batchNo || "-",
        getTooltipText: (item) => item.batchNo || "-",
      },
      {
        id: "manufacturer",
        header: "Manufacturer",
        width: "minmax(180px, 1fr)",
        render: (item) => getManufacturerDisplay(item),
        getTooltipText: (item) => getManufacturerDisplay(item),
      },
    ],
    [handlePatientClick]
  );

  const rowActions = useMemo(
    () => [
      {
        label: "View status details",
        icon: AlertTriangle,
        primary: true,
        primaryOnly: true,
        // Was compared against the bare strings "warning" and "error", which the
        // backend never sends — so this action never rendered, for any row, in any
        // state (KIN-1719 §2). The vocabulary lives in utils/immunizationStatus.
        visibleWhen: (item) =>
          isImmunizationRejected(item.status) ||
          isImmunizationPending(item.status) ||
          isImmunizationActionRequired(item.status),
        color: (item) =>
          isImmunizationRejected(item.status) ? "#b70000" : "#d97706",
        bg: (item) =>
          isImmunizationRejected(item.status) ? "#ffe8e8" : "#fef3c7",
        border: (item) =>
          isImmunizationRejected(item.status) ? "#b70000" : "#fbbf24",
        onClick: (item) => setDetailsItem(item),
      },
      {
        label: "Submit to Healthlink",
        icon: Send,
        primary: true,
        visibleWhen: canSubmitToHealthlink,
        onClick: (item) =>
          submitVaccine({
            immunisationId: item.id,
            administrationMethod: item.administrationMethod,
            patientId: item.patient?.id,
          }),
        disabled: isSubmitting,
      },
      {
        label: "See details",
        icon: Eye,
        onClick: (item) => setDetailsItem(item),
      },
      {
        label: "Download immunisation report",
        icon: Download,
        onClick: handleDownloadSingle,
      },
      { label: "Edit", icon: Pencil, onClick: (item) => setEditItem(item) },
      {
        label: "Cancel vaccine",
        icon: Ban,
        onClick: (item) => setCancelItem(item),
      },
    ],
    [handleDownloadSingle, submitVaccine, isSubmitting, setDetailsItem]
  );

  const totalRecords = data?.totalRecords || 0;
  const hasFilters = search || Object.values(advancedFilters).some((v) => v);

  return (
    <>
      <DataTable
        columns={columns}
        data={data?.data || []}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        emptyTitle="No Healthlink immunisations found"
        emptyText={
          hasFilters
            ? "Try adjusting your search or filters to find what you're looking for."
            : "No Healthlink immunisation records to display at this time."
        }
        search={{
          value: search,
          onChange: handleSearchChange,
          placeholder: "Search by patient name",
        }}
        sort={{ field: sortField, direction: sortDirection }}
        sortOptions={SORT_OPTIONS}
        onSortChange={handleSortChange}
        rowActions={rowActions}
        currentPage={currentPage}
        rowsPerPage={rows}
        rowsValue={rows}
        onRowsChange={(val) => {
          setRows(val);
          setCurrentPage(1);
        }}
        totalCount={totalRecords}
        onPageChange={setCurrentPage}
        actionsColumnWidth="112px"
        getRowClassName={(row) => {
          // Settled and withdrawn: struck through, still legible. Checked first because it
          // is terminal — nothing about the original submission matters once both
          // receivers have confirmed the cancellation (KIN-1815).
          if (isImmunizationCancelled(row.status)) return "cancelled";
          // Same dead comparison as the row action above — rejected rows were never
          // highlighted (KIN-1719 §2). A refused cancellation lands here too: the dose is
          // still on a national register while the practice believes it withdrawn.
          if (isImmunizationRejected(row.status)) return "error";
          if (isImmunizationPending(row.status)) return "warning";
          // Sent and unanswered, or never sent. Amber like pending — it is not a refusal — but it
          // must not be silent, because these are the rows nothing will resolve on its own.
          if (isImmunizationActionRequired(row.status)) return "warning";

          return null;
        }}
      />

      <Dialog
        open={!!detailsItem}
        title="Vaccine details"
        onClose={() => setDetailsItem(null)}
        width={1050}
      >
        {detailsItem && (
          <VaccineDetails
            onOpenEditClick={() => {
              setEditItem(detailsItem);
              setDetailsItem(null);
            }}
            vaccine={detailsItem}
            closeModal={() => setDetailsItem(null)}
            tenantVal={tenantVal}
            patientId={detailsItem.patient?.id}
            patient={detailsItem.patient}
          />
        )}
      </Dialog>

      <Dialog
        open={!!editItem}
        title="Edit vaccine"
        onClose={() => setEditItem(null)}
        width={1200}
      >
        {editItem && (
          <AddVaccineForm
            currentPatientId={editItem.patient?.id}
            closeModal={() => setEditItem(null)}
            tenantVal={tenantVal}
            vaccine={editItem}
          />
        )}
      </Dialog>

      <PpsnClaimBlockedDialog
        patientId={ppsnBlockedPatientId}
        onClose={clearPpsnBlocked}
      />

      <CancelImmunisationModal
        isOpen={!!cancelItem}
        onClose={() => setCancelItem(null)}
        onConfirm={() => {
          if (cancelItem) {
            deleteImmunisation(cancelItem.id, {
              onSuccess: () => setCancelItem(null),
            });
          }
        }}
        isLoading={isDeletingImmunisation}
      />
    </>
  );
};
